import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';

type AuthState = {
  user: User | null;
  status: 'loading' | 'signed-out' | 'signed-in';
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Surface an error from a redirect sign-in that failed after returning.
    getRedirectResult(auth).catch((e: unknown) => setError(messageFor(e)));

    return onAuthStateChanged(
      auth,
      (u) => {
        setUser(u);
        setReady(true);
      },
      (e) => {
        setError(messageFor(e));
        setReady(true);
      },
    );
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      status: !ready ? 'loading' : user ? 'signed-in' : 'signed-out',
      error,
      async signIn() {
        setError(null);
        try {
          await signInWithPopup(auth, googleProvider);
        } catch (e: unknown) {
          // Popups are blocked in installed PWAs and some in-app browsers —
          // both places this app is likely to be opened. Fall back rather than
          // leaving the parent stuck on a dead button.
          if (isPopupProblem(e)) {
            await signInWithRedirect(auth, new GoogleAuthProvider());
            return;
          }
          setError(messageFor(e));
        }
      },
      async signOut() {
        await fbSignOut(auth);
      },
    }),
    [user, ready, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

function codeOf(e: unknown): string {
  return typeof e === 'object' && e !== null && 'code' in e ? String((e as { code: unknown }).code) : '';
}

function isPopupProblem(e: unknown): boolean {
  const code = codeOf(e);
  return (
    code === 'auth/popup-blocked' ||
    code === 'auth/operation-not-supported-in-this-environment' ||
    code === 'auth/popup-closed-by-user'
  );
}

function messageFor(e: unknown): string | null {
  const code = codeOf(e);
  if (!code) return e instanceof Error ? e.message : null;
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return null; // the parent backed out on purpose
    case 'auth/network-request-failed':
      return 'ネットワークにつながりませんでした。電波を確認してもう一度どうぞ。';
    case 'auth/unauthorized-domain':
      return 'このドメインは Firebase Authentication で許可されていません。';
    default:
      return 'サインインできませんでした。もう一度おためしください。';
  }
}
